import React from "react";
import { createBaseBlock } from "./BaseBlock";
import { Table, Typography } from "antd";
import { ActivityItem } from "../components/ActivityItem";
import { stripMarkdown } from "../cardUtils";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface Activity {
  title: string;
  type: string;
  status: string;
  contributors: string[];
  evidence?: string[];
}

// Activities Block Recipe
const activitiesRecipe = {
  id: "activities-block",
  name: "Activities Block",
  globalKey: [],
  prefix: `[
    {
      "title": "string",
      "type": "string — e.g. 'issue', 'pull_request', 'theme'",
      "status": "string — e.g. 'open', 'closed', 'merged'",
      "contributors": ["string — names involved"],
      "evidence": ["string — supporting PRs, issues, themes, metrics"]
    }
  ]`,
  format: {
    type: "json",
    fields: ["description"],
    constraints: [{ field: "description", type: "string", required: true }],
  },
  validationRules: [
    {
      type: "json_structure",
      condition: (data: any): boolean => {
        try {
          const parsed = JSON.parse(data) as Activity[];
          return Array.isArray(parsed) && parsed.every((item) => item.title);
        } catch {
          return false;
        }
      },
      message: "Response must be valid JSON with required fields",
    },
  ],
};

// Activities Block Parser
function activitiesParser(data: any) {
  try {
    return {
      data: data[activitiesBlock.id],
    };
  } catch (error) {
    return {
      error: true,
      message: `Failed to parse activities data: ${
        error instanceof Error ? error.message : String(error)
      }`,
      rawContent: data,
    };
  }
}

// Activities Block Variants
const activitiesVariants = [
  {
    id: "table",
    name: "Table",
    description: "Table view of activities",
  },
  {
    id: "list",
    name: "List",
    description: "List view of activities",
  },
  {
    id: "grid",
    name: "Grid",
    description: "Grid/card view of activities",
  },
];

// Activities Block Renderer
function ActivitiesBlockRenderer(props: any) {
  const { data, variant, className, style } = props;
  if (data === undefined) return null;
  const activitiesData = data[activitiesBlock.id];
  if (!activitiesData || activitiesData.length === 0) return null;

  // Table Variant
  if (variant === "table") {
    return (
      <div className="vzbl-panel-container">
        <Typography.Title level={4} className="vzbl-header-4">
          Activities
        </Typography.Title>
        <Table
          dataSource={activitiesData}
          pagination={false}
          rowKey={(row) => row.title + row.type}
          size="small"
          style={{ background: "none" }}
          columns={[
            {
              title: "Title",
              dataIndex: "title",
              key: "title",
              render: (text: string) => (
                <Typography.Text className="vzbl-header-5 single-line">
                  <Markdown remarkPlugins={[remarkGfm]} className="vzbl-text-3">
                    {text}
                  </Markdown>
                </Typography.Text>
              ),
            },
            {
              title: "Status",
              dataIndex: "status",
              key: "status",
              render: (text: string) => (
                <Typography.Text className="vzbl-text-3">
                  {stripMarkdown(text)}
                </Typography.Text>
              ),
            },
            {
              title: "Contributors",
              dataIndex: "contributors",
              key: "contributors",
              render: (contributors: string[]) =>
                contributors && contributors.length > 0
                  ? contributors.map((c, i) => (
                      <Typography.Text
                        className="vzbl-text-3"
                        key={i}
                        style={{ marginRight: 4 }}
                      >
                        <Markdown
                          remarkPlugins={[remarkGfm]}
                          className="vzbl-text-3"
                        >
                          {c}
                        </Markdown>
                      </Typography.Text>
                    ))
                  : null,
            },
          ]}
        />
      </div>
    );
  }

  // List Variant
  if (variant === "list") {
    return (
      <div className="vzbl-panel-container">
        <Typography.Title level={4} className="vzbl-header-4">
          Activities
        </Typography.Title>
        <div>
          {activitiesData.map((activity: Activity, idx: number) => (
            <ActivityItem
              key={`activity-list-${idx}`}
              description={activity.title}
              tags={activity.contributors}
              cta={activity.status}
              evidence={activity.evidence}
              variant="list"
              className="vzbl-panel-overlay vzbl-panel-shadow"
            />
          ))}
        </div>
      </div>
    );
  }

  // Grid Variant (default)
  return (
    <div className="vzbl-panel-container">
      <Typography.Title level={4} className="vzbl-header-4">
        Activities
      </Typography.Title>
      <div className="responsive-card-grid">
        {activitiesData.map((activity: Activity, idx: number) => (
          <ActivityItem
            key={`activity-grid-${idx}`}
            description={activity.title}
            tags={activity.contributors}
            cta={activity.status}
            evidence={activity.evidence}
            variant="default"
            className="vzbl-panel-overlay vzbl-panel-shadow"
          />
        ))}
      </div>
    </div>
  );
}

// Create Activities Block
const activitiesBlock = createBaseBlock({
  id: "activities",
  name: "Activities Block",
  description: "Displays activities in table, list, or grid format",
  recipe: activitiesRecipe,
  variants: activitiesVariants,
  parser: activitiesParser,
  renderer: ActivitiesBlockRenderer,
});

export { activitiesBlock };
